/**
 * The head map seen from above: contacts named by where they sit, keyed on
 * channel name. Geometry carried from Canvas 05 (BrainSpot Web - Device):
 * the 80 × 98 band ellipse, contact angles, and the leader-line formula.
 * Ported from `calibrations/static/capture/headmap.mjs`; the React component
 * (`components/capture/HeadMap.tsx`) draws from `layout()` and `rows()`.
 *
 * Pure: no DOM.
 */

import { WORDS, type Tier } from './quality'

export const VIEWBOX = '0 0 460 340'
export const CX = 230
export const CY = 175
export const RX = 80 // band ellipse
export const RY = 98
const LABEL_RX = 132 // label anchor ellipse
const LABEL_RY = 140
const LEADER_FRACTION = 0.82

export interface ContactSeat {
  channel: string
  loc: string
  angle: number
}

/** Canvas 05 contacts, in the canvas's own order, keyed on hardware channel. */
export const CONTACTS: readonly ContactSeat[] = Object.freeze([
  Object.freeze({ channel: 'Fp1', loc: 'Left forehead', angle: -115 }),
  Object.freeze({ channel: 'Fp2', loc: 'Right forehead', angle: -65 }),
  Object.freeze({ channel: 'T3', loc: 'Above left ear', angle: 180 }),
  Object.freeze({ channel: 'T4', loc: 'Above right ear', angle: 0 }),
  Object.freeze({ channel: 'T5', loc: 'Behind left ear', angle: 144 }),
  Object.freeze({ channel: 'T6', loc: 'Behind right ear', angle: 36 }),
  Object.freeze({ channel: 'O1', loc: 'Back left', angle: 115 }),
  Object.freeze({ channel: 'O2', loc: 'Back right', angle: 65 }),
])

// Modern 10-20 spellings map onto the same seats. The app writes the legacy
// names the device reports; the backend renames T3→T7 etc. internally.
const ALIASES: Readonly<Record<string, string>> = Object.freeze({ T7: 'T3', T8: 'T4', P7: 'T5', P8: 'T6' })

const BY_CHANNEL = new Map(CONTACTS.map((c) => [c.channel, c]))

function round1(v: number): number {
  return Math.round(v * 10) / 10
}

export interface Placement {
  x: number
  y: number
  lx: number
  ly: number
  lx2: number
  ly2: number
  anchor: 'start' | 'end'
}

/** Position for one contact angle, per the canvas formula. */
export function placeAngle(angle: number): Placement {
  const r = (angle * Math.PI) / 180
  const c = Math.cos(r)
  const s = Math.sin(r)
  const x = CX + RX * c
  const y = CY + RY * s
  const ax = CX + LABEL_RX * c
  const ay = CY + LABEL_RY * s
  return {
    x: round1(x),
    y: round1(y),
    lx: round1(ax),
    ly: round1(ay + 4),
    lx2: round1(x + (ax - x) * LEADER_FRACTION),
    ly2: round1(y + (ay - y) * LEADER_FRACTION),
    anchor: c < -0.01 ? 'end' : 'start',
  }
}

export interface PlacedContact extends Placement {
  channel: string
  seat: string
  loc: string
  angle: number
}

export interface Layout {
  placed: PlacedContact[]
  unplaced: string[]
}

/**
 * layout(channels) → { placed, unplaced }
 * Known channels get a seat; unknown ones (EOG, Cz, …) are listed as
 * unplaced and never guessed at. Display order is preserved.
 */
export function layout(channels: readonly string[] | null | undefined): Layout {
  const placed: PlacedContact[] = []
  const unplaced: string[] = []
  const seen = new Set<string>()
  for (const raw of channels ?? []) {
    const name = String(raw)
    const key = ALIASES[name] ?? name
    const seat = BY_CHANNEL.get(key)
    if (!seat || seen.has(key)) {
      unplaced.push(name)
      continue
    }
    seen.add(key)
    placed.push({ channel: name, seat: key, loc: seat.loc, angle: seat.angle, ...placeAngle(seat.angle) })
  }
  return { placed, unplaced }
}

/** "8 of 8 contacts placed" / "4 of 4 contacts placed · 1 not placed". */
export function legendText(lay: Layout): string {
  const n = lay.placed.length
  const base = `${n} of ${n} contact${n === 1 ? '' : 's'} placed`
  if (!lay.unplaced.length) return base
  return `${base} · ${lay.unplaced.length} not placed`
}

export interface ContactRow {
  channel: string
  loc: string
  tier: Tier
  word: string
  placed: boolean
}

/** Rows for the list beside the map: every contact gets a word. */
export function rows(lay: Layout, tiersByChannel: Readonly<Record<string, Tier>> | null | undefined): ContactRow[] {
  const out: ContactRow[] = []
  for (const p of lay.placed) {
    const tier = tiersByChannel?.[p.channel] ?? 'none'
    out.push({ channel: p.channel, loc: p.loc, tier, word: WORDS[tier], placed: true })
  }
  for (const ch of lay.unplaced) {
    const tier = tiersByChannel?.[ch] ?? 'none'
    out.push({ channel: ch, loc: ch, tier, word: WORDS[tier], placed: false })
  }
  return out
}
